import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Users, Handshake, CheckCircle, ArrowRight, Gift, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';

const PAYPAL_LINK = "https://www.paypal.com/donate/?hosted_button_id=ZUX3BKRN524VS";

export default function TakeActionPage() {
  return (
    <div data-testid="take-action-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E5B75F] via-[#E8C06F] to-[#EBCB7F]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-white/20 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#E08B7A]/30 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-slate-900 mb-6">
              Call to Action
            </h1>
            <p className="text-2xl text-slate-800 font-semibold">
              Be Part of the Change — Support Our Work
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[500px] h-[450px] bg-[#E08B7A]/10 rounded-br-[250px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[350px] bg-[#4A7C59]/10 rounded-tl-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-slate-700 leading-relaxed text-lg mb-6">
              At the <strong className="text-[#4A7C59]">Organization for Poverty Alleviation and Development (OPAD)</strong>, we believe that lasting change is made possible through collective action. Whether you choose to donate, volunteer, partner, or participate in our global programmes, your support helps empower vulnerable communities and advance sustainable development initiatives worldwide.
            </p>
            <p className="text-slate-700 leading-relaxed text-lg">
              From humanitarian relief and community food distribution in <strong className="text-[#7BC4E2]">Botkyrka, Sweden</strong>, to youth empowerment and digital skills training programmes across <strong className="text-[#E08B7A]">Africa and beyond</strong>, your contribution enables us to reach those who need it most.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Donate Section */}
      <section className="relative section-padding overflow-hidden bg-[#FDF8F4]">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[450px] h-[400px] bg-[#E08B7A]/15 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[350px] h-[300px] bg-[#7BC4E2]/10 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-[#E08B7A] rounded-2xl flex items-center justify-center">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl font-bold font-['Manrope'] text-slate-900">Donate to Make a Difference</h2>
              </div>
              
              <p className="text-slate-700 mb-6 text-lg">Your financial support helps OPAD:</p>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#E08B7A] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Deliver humanitarian assistance to underserved communities</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#E08B7A] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Support youth skills development and employment initiatives</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#E08B7A] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Promote gender equality and social inclusion</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#E08B7A] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Implement climate action and environmental sustainability programmes</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#E08B7A] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Expand access to education and digital learning opportunities</span>
                </li>
              </ul>
              
              <p className="text-slate-600 italic mb-6">
                Every contribution—big or small—brings us closer to building resilient communities and creating sustainable futures.
              </p>
              
              <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#E08B7A] hover:bg-[#D07A69] text-white rounded-full px-10 py-6 text-lg font-semibold">
                  <Heart className="w-5 h-5 mr-2" />
                  Donate Now via PayPal
                </Button>
              </a>
            </motion.div>
            
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-xl border border-[#E08B7A]/20"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Gift className="w-16 h-16 text-[#E08B7A] mx-auto mb-6" />
              <h3 className="text-2xl font-bold font-['Manrope'] text-slate-900 text-center mb-4">
                Your Impact
              </h3>
              <div className="space-y-4">
                <div className="bg-[#E08B7A]/5 p-4 rounded-xl">
                  <p className="text-[#E08B7A] font-bold">$25</p>
                  <p className="text-slate-600 text-sm">Provides school supplies for 5 children</p>
                </div>
                <div className="bg-[#7BC4E2]/5 p-4 rounded-xl">
                  <p className="text-[#7BC4E2] font-bold">$50</p>
                  <p className="text-slate-600 text-sm">Feeds a family for one month</p>
                </div>
                <div className="bg-[#4A7C59]/5 p-4 rounded-xl">
                  <p className="text-[#4A7C59] font-bold">$100</p>
                  <p className="text-slate-600 text-sm">Supports digital skills training for 3 youth</p>
                </div>
                <div className="bg-[#E5B75F]/5 p-4 rounded-xl">
                  <p className="text-[#E5B75F] font-bold">$250</p>
                  <p className="text-slate-600 text-sm">Sponsors a community development project</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Get Involved Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[450px] h-[400px] bg-[#7BC4E2]/10 rounded-br-[200px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[300px] bg-[#4A7C59]/10 rounded-tl-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-xl border border-[#7BC4E2]/20 order-2 lg:order-1"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Globe className="w-16 h-16 text-[#7BC4E2] mx-auto mb-6" />
              <h3 className="text-2xl font-bold font-['Manrope'] text-slate-900 text-center mb-4">
                Ways to Get Involved
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 bg-[#7BC4E2]/5 p-4 rounded-xl">
                  <Users className="w-8 h-8 text-[#7BC4E2]" />
                  <div>
                    <p className="font-bold text-slate-900">Volunteer</p>
                    <p className="text-slate-600 text-sm">Join community outreach programmes</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-[#E08B7A]/5 p-4 rounded-xl">
                  <Users className="w-8 h-8 text-[#E08B7A]" />
                  <div>
                    <p className="font-bold text-slate-900">Internship</p>
                    <p className="text-slate-600 text-sm">Apply for international opportunities</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-[#4A7C59]/5 p-4 rounded-xl">
                  <Handshake className="w-8 h-8 text-[#4A7C59]" />
                  <div>
                    <p className="font-bold text-slate-900">Partner</p>
                    <p className="text-slate-600 text-sm">Collaborate on development initiatives</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-[#E5B75F]/5 p-4 rounded-xl">
                  <Globe className="w-8 h-8 text-[#E5B75F]" />
                  <div>
                    <p className="font-bold text-slate-900">Participate</p>
                    <p className="text-slate-600 text-sm">Join global events and campaigns</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              className="order-1 lg:order-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-[#7BC4E2] rounded-2xl flex items-center justify-center">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl font-bold font-['Manrope'] text-slate-900">Get Involved</h2>
              </div>
              
              <p className="text-slate-700 mb-6 text-lg">Join our global movement by:</p>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#7BC4E2] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Volunteering in community outreach programmes</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#7BC4E2] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Applying for international internship opportunities</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#7BC4E2] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Partnering with OPAD on development initiatives</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#7BC4E2] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Participating in global events and campaigns</span>
                </li>
              </ul>
              
              <p className="text-slate-600 italic mb-6">
                Together, we can empower lives, strengthen communities, and promote inclusive development across regions.
              </p>
              
              <Link to="/get-involved">
                <Button className="bg-[#7BC4E2] hover:bg-[#6AB3D1] text-slate-900 rounded-full px-10 py-6 text-lg font-semibold">
                  <Users className="w-5 h-5 mr-2" />
                  Become a Volunteer
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Take Action Today */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A7C59] via-[#5A8C69] to-[#6B9B7A]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-white/10 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#E5B75F]/20 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-8">
              Take Action Today
            </h2>
            
            <div className="flex flex-col md:flex-row gap-6 justify-center">
              <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#E5B75F] hover:bg-[#D4A64E] text-slate-900 rounded-full px-10 py-6 text-lg font-semibold min-w-[220px]">
                  <Heart className="w-5 h-5 mr-2" />
                  Donate Now
                </Button>
              </a>
              <Link to="/get-involved">
                <Button className="bg-[#7BC4E2] hover:bg-[#6AB3D1] text-slate-900 rounded-full px-10 py-6 text-lg font-semibold min-w-[220px]">
                  <Users className="w-5 h-5 mr-2" />
                  Become a Volunteer
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="bg-white hover:bg-slate-100 text-[#4A7C59] rounded-full px-10 py-6 text-lg font-semibold min-w-[220px]">
                  <Handshake className="w-5 h-5 mr-2" />
                  Partner With Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
